import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, Tag, Calendar } from "lucide-react";
import SEO from "@/components/SEO";
import CtaBanner from "@/components/CtaBanner";
import { BLOG_POSTS } from "@/data/blog";
import { BLOG_SEO_TITLES, BLOG_SEO_DESCRIPTIONS } from "@/data/routeSeo";
import { ORG_ID } from "@/data/schema";
import { canonicalFor } from "@/lib/site";
import { sized, srcSet } from "@/lib/images";
import NotFound from "@/pages/NotFound";

// "Dec 18, 2025" -> "2025-12-18" (schema.org dates must be ISO 8601).
const MONTHS = { Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6, Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12 };
const isoDate = (d) => {
  const m = /^([A-Z][a-z]{2}) (\d{1,2}), (\d{4})$/.exec(d || "");
  return m ? `${m[3]}-${String(MONTHS[m[1]]).padStart(2, "0")}-${m[2].padStart(2, "0")}` : d;
};

function renderMarkdown(md) {
  // Lightweight markdown rendering: ##, **bold**, lists, paragraphs, links
  const lines = md.split("\n");
  const out = [];
  let listBuf = [];
  let listType = null;

  const flushList = () => {
    if (listBuf.length === 0) return;
    if (listType === "ul") {
      out.push(
        <ul key={`ul-${out.length}`} className="my-5 list-disc space-y-2 pl-6 text-black/85">
          {listBuf.map((it, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: inline(it) }} />
          ))}
        </ul>
      );
    } else {
      out.push(
        <ol key={`ol-${out.length}`} className="my-5 list-decimal space-y-2 pl-6 text-black/85">
          {listBuf.map((it, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: inline(it) }} />
          ))}
        </ol>
      );
    }
    listBuf = [];
    listType = null;
  };

  const inline = (txt) =>
    txt
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>")
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="underline decoration-[#FFD700] decoration-2 underline-offset-4 hover:bg-[#FFD700]">$1</a>');

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flushList();
      continue;
    }
    if (line.startsWith("## ")) {
      flushList();
      out.push(
        <h2 key={`h2-${out.length}`} className="display mt-12 text-2xl font-black tracking-tight md:text-3xl">
          {line.slice(3)}
        </h2>
      );
    } else if (line.startsWith("# ")) {
      flushList();
      out.push(
        <h1 key={`h1-${out.length}`} className="display mt-10 text-3xl font-black md:text-4xl">
          {line.slice(2)}
        </h1>
      );
    } else if (/^- /.test(line)) {
      if (listType !== "ul") flushList();
      listType = "ul";
      listBuf.push(line.slice(2));
    } else if (/^\d+\.\s/.test(line)) {
      if (listType !== "ol") flushList();
      listType = "ol";
      listBuf.push(line.replace(/^\d+\.\s/, ""));
    } else {
      flushList();
      out.push(
        <p
          key={`p-${out.length}`}
          className="my-4 text-base leading-relaxed text-black/85 md:text-lg"
          dangerouslySetInnerHTML={{ __html: inline(line) }}
        />
      );
    }
  }
  flushList();
  return out;
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  if (!post) return <NotFound />;

  const others = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 3);

  const url = canonicalFor(`/blog/${post.slug}`);
  const articleSchema = {
    "@type": "BlogPosting",
    "@id": `${url}#article`,
    "headline": post.title,
    "description": post.excerpt,
    "image": sized(post.cover, 1200),
    "author": { "@id": ORG_ID },
    "publisher": { "@id": ORG_ID },
    "datePublished": isoDate(post.date),
    "dateModified": isoDate(post.updated || post.date),
    "inLanguage": "en",
    "keywords": post.keywords?.join(", "),
    "mainEntityOfPage": { "@id": `${url}#webpage` }
  };

  return (
    <>
      <SEO
        title={BLOG_SEO_TITLES[post.slug] || post.title}
        description={BLOG_SEO_DESCRIPTIONS[post.slug] || post.excerpt}
        keywords={post.keywords?.join(", ")}
        image={sized(post.cover, 1200)}
        type="article"
        path={`/blog/${post.slug}`}
        schema={articleSchema}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blog" },
          { name: post.title, path: `/blog/${post.slug}` }
        ]}
      />

      <article className="bg-white">
        <div className="border-b-2 border-black bg-[#FAFAF7]">
          <div className="mx-auto max-w-3xl px-4 py-12 md:px-8 md:py-16">
            <Link to="/blog/" data-testid="blog-back" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest hover:text-black/60">
              <ArrowLeft size={14} /> All articles
            </Link>
            <div className="mt-6 flex flex-wrap items-center gap-3 text-[11px] font-bold uppercase tracking-widest text-black/60">
              <span className="flex items-center gap-1"><Tag size={12} /> {post.category}</span>
              <span>·</span>
              <span className="flex items-center gap-1"><Clock size={12} /> {post.readTime}</span>
              <span>·</span>
              <span className="flex items-center gap-1"><Calendar size={12} /> <time dateTime={isoDate(post.date)}>{post.date}</time></span>
              <span>·</span>
              <span>By the Orina Enterprise team</span>
            </div>
            <h1 data-testid="blog-post-title" className="display mt-6 text-4xl font-black leading-[1.05] tracking-tight md:text-5xl lg:text-6xl">
              {post.title}
            </h1>
            <p className="mt-5 text-lg text-black/75">{post.excerpt}</p>
          </div>
        </div>

        <div className="mx-auto max-w-3xl px-4 py-12 md:px-8 md:py-16">
          <div className="card-brutal mb-10 overflow-hidden p-0">
            <img src={sized(post.cover, 1200)} srcSet={srcSet(post.cover)} sizes="(min-width: 768px) 704px, 100vw" width="1200" height="675" alt={post.title} className="aspect-[16/9] w-full object-cover" />
          </div>
          <div className="prose-orina">{renderMarkdown(post.body)}</div>
        </div>
      </article>

      <section className="border-y-2 border-black bg-[#FAFAF7] py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <h3 className="display text-2xl font-black tracking-tight md:text-3xl">Keep reading</h3>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {others.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}/`} className="card-brutal flex flex-col overflow-hidden p-0">
                <div className="aspect-[16/10] overflow-hidden border-b-2 border-black">
                  <img src={sized(p.cover, 640)} width="640" height="400" loading="lazy" decoding="async" alt={p.title} className="h-full w-full object-cover" />
                </div>
                <div className="flex-1 p-6">
                  <div className="text-[11px] font-bold uppercase tracking-widest text-black/60">{p.category}</div>
                  <h4 className="display mt-2 text-lg font-black leading-snug">{p.title}</h4>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner />
    </>
  );
}
